import React from 'react';
import './LegalModal.css';

const TermsModal = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content legal-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Terms of Service</h2>
                    <button className="close-button" onClick={onClose}>&times;</button>
                </div>

                <div className="legal-content">
                    <p className="last-updated">Last Updated: December 30, 2025</p>

                    <section className="legal-section">
                        <h3>1. Acceptance of Terms; Who We Are; Definitions</h3>
                        <p>
                            By accessing or using Jeffrey Discs (jeffreydiscs.org) ("the App"), you agree to these Terms of Service
                            ("Terms"). If you do not agree, do not use the App. The App is owned and operated by School 21 LLC dba
                            DoZESQ Imaginarium ("DoZESQ Imaginarium," "DoZESQ," "we," "us," or "our"). We created this project in
                            collaboration with the Jeffrey Lucas Gurmendi Marshall Foundation (the "Foundation"), which shares our
                            mission of fostering joy, kindness, and mental well-being in youth and families. The Foundation is not
                            the legal owner or operator of the App. You must be at least 13 years old to submit content.
                        </p>
                        <p>
                            In these Terms, "Content" means anything you submit or upload (text, photos, videos, and related information).
                            "Family-Only Content" means Content designated to be visible only to authenticated family members (as determined
                            by DoZESQ).
                        </p>
                    </section>

                    <section className="legal-section">
                        <h3>2. Accounts and Access</h3>
                        <p>
                            You can browse most disc information without an account. Certain "Family-Only" areas require sign-in by
                            approved family members. We reserve the right to determine who qualifies as a family member for access purposes.
                            You are responsible for any activity associated with your account and for keeping your account information accurate.
                            We may suspend or terminate access if we believe your use violates these Terms or harms the App or community.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h3>3. User Content (Ownership and License)</h3>
                        <p>
                            You may submit or upload Content to the App. <strong>You retain any ownership rights you have in your Content.</strong>{' '}
                            By submitting Content, you grant DoZESQ a non-exclusive, worldwide, royalty-free, sublicensable and transferable
                            license to host, store, reproduce, display, distribute, and modify (for formatting/compatibility) your Content
                            as needed to operate, maintain, secure, improve, and promote the App, consistent with the visibility setting you choose.
                        </p>
                        <p>
                            You represent and warrant that you own or have the rights and permissions needed to submit the Content and that your
                            Content does not violate others’ rights (including privacy, publicity, or intellectual property rights).
                        </p>
                    </section>

                    <section className="legal-section">
                        <h3>4. Visibility: Public vs. Family Only</h3>
                        <ul>
                            <li><strong>Public:</strong> Visible to anyone who visits the App and may be shared by others outside the App</li>
                            <li><strong>Family Only:</strong> Visible only to authenticated family members (as determined by DoZESQ)</li>
                        </ul>
                        <p>
                            Once approved (if moderation is used), your Content will be displayed according to the visibility setting you chose.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h3>5. Content Standards</h3>
                        <p>You agree not to submit Content that is:</p>
                        <ul>
                            <li>Unlawful, threatening, harassing, hateful, or otherwise inappropriate for a memorial/community environment</li>
                            <li>Spam, scams, commercial solicitations, or advertising</li>
                            <li>Infringing on intellectual property or privacy/publicity rights</li>
                            <li>Contains personal information about others without consent</li>
                            <li>Malicious (including viruses, harmful code, or attempts to disrupt the App)</li>
                        </ul>
                    </section>

                    <section className="legal-section">
                        <h3>6. Moderation and Removal</h3>
                        <p>
                            We may review, approve, reject, remove, restrict, or edit Content (for formatting/clarity) at our discretion.
                            We may suspend or terminate access for violations of these Terms. We do not guarantee that all inappropriate
                            Content will be caught before display.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h3>7. Location Data</h3>
                        <p>
                            Disc golf course locations and journey markers are public by design and are essential to tracking disc journeys.
                            Do not submit location information you wish to keep private.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h3>8. Email Communications (Optional Subscription)</h3>
                        <p>By subscribing to email updates:</p>
                        <ul>
                            <li>You agree to receive monthly digest emails about disc activity</li>
                            <li>You agree to receive occasional emails about other App updates (such as annual commemorative disc golf rounds)</li>
                            <li>Our goal is to send no more than one digest email per month, with other emails only occasionally</li>
                            <li>You can unsubscribe at any time via the link in any email</li>
                            <li>We do not sell your email address and do not send third-party marketing emails</li>
                        </ul>
                    </section>

                    <section className="legal-section">
                        <h3>9. Intellectual Property</h3>
                        <p>
                            The App name, logos, software, platform design and other materials belong to DoZESQ Imaginarium or its licensors.
                            You may not use them without permission.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h3>10. Third-Party Services</h3>
                        <p>
                            The App may rely on third-party services (for example, hosting/authentication/email and mapping). Those third parties
                            have their own terms and policies. We are not responsible for their services.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h3>11. Disclaimer of Warranties</h3>
                        <p>
                            The App is provided "as is" and "as available" without warranties of any kind. We do not guarantee that the App will be
                            uninterrupted or error-free, or that Content will always be preserved without loss.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h3>12. Limitation of Liability</h3>
                        <p>
                            To the fullest extent permitted by law, School 21 LLC and DoZESQ Imaginarium are not liable for any indirect,
                            incidental, special, consequential, or punitive damages arising from your use of the App.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h3>13. Changes to Terms</h3>
                        <p>
                            We may update these Terms at any time. The "Last Updated" date above indicates the most recent version.
                            Continued use of the App after changes constitutes acceptance of the updated Terms.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h3>14. Contact</h3>
                        <p>
                            Questions about these Terms? Contact us at{' '}
                            <a href="mailto:imagine@dozesq.com">imagine@dozesq.com</a>.
                        </p>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default TermsModal;
